import {
    defaultIConfig,
    IChat,
    IChatRequest,
    IConfig,
    IMessage,
    IProject,
    IPrompt,
    IShare,
    ISummary,
} from "@/lib/types";
import { generateMockPrompts } from "@/lib/mocks";
import { CHAT_API_URL, GRAPHQL_API_URL } from "@/lib/utils";

export const getChats = async (jwt: string, offset = 0, limit = 50): Promise<IChat[]> => {
    const query = `
        query GetChats($limit: Int, $offset: Int) {
            chats(limit: $limit, offset: $offset) {
                chatid
                email
                title
                favorite
                shareid
                project {
                    projectid
                }
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { limit, offset },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.chats.map(
        (chat: {
            chatid: string;
            email: string;
            title?: string;
            favorite: boolean;
            shareid?: string;
            project?: { projectid: string };
            created: string;
            lastmessage: string;
        }): IChat => ({
            chatid: chat.chatid,
            projectid: chat.project?.projectid,
            user: chat.email,
            title: chat.title,
            favorite: chat.favorite,
            shareid: chat.shareid,
            messages: [],
            created: new Date(chat.created),
            lastmessage: new Date(chat.lastmessage),
        })
    );
};

export const getChat = async (jwt: string, chatid: string): Promise<IChat | null> => {
    const query = `
        query GetChat($chatid: String!) {
            chat(chatid: $chatid) {
                chatid
                email
                title
                favorite
                shareid
                project {
                    projectid
                }
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const chat = result.data.chat;

    if (!chat) {
        return null;
    }

    return {
        chatid: chat.chatid,
        projectid: chat.project?.projectid,
        user: chat.email,
        title: chat.title,
        favorite: chat.favorite,
        shareid: chat.shareid,
        messages: [],
        created: new Date(chat.created),
        lastmessage: new Date(chat.lastmessage),
    };
};

export const getChatsByProjectId = async (jwt: string, projectid: string): Promise<IChat[]> => {
    const query = `
        query GetChatsByProjectId($projectid: String!) {
            project(projectid: $projectid) {
                chats {
                    chatid
                    email
                    title
                    favorite
                    shareid
                    created
                    lastmessage
                }
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { projectid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Return empty array if project not found
    if (!result.data.project) {
        return [];
    }

    // Map GraphQL response to IChat interface
    return result.data.project.chats.map(
        (chat: {
            chatid: string;
            email: string;
            title?: string;
            favorite: boolean;
            shareid?: string;
            created: string;
            lastmessage: string;
        }): IChat => ({
            chatid: chat.chatid,
            projectid: projectid,
            user: chat.email,
            title: chat.title,
            favorite: chat.favorite,
            shareid: chat.shareid,
            created: new Date(chat.created),
            lastmessage: new Date(chat.lastmessage),
        })
    );
};

export const saveConfig = (config: IConfig) => {
    if (window && window.localStorage) {
        window.localStorage.setItem("FAIconfig", JSON.stringify(config));
    }
};

export const loadConfig = (): IConfig => {
    if (typeof window !== "undefined") {
        return JSON.parse(window.localStorage.getItem("FAIconfig") || JSON.stringify(defaultIConfig)) as IConfig;
    } else {
        return {} as IConfig;
    }
};

export const updateChat = async (jwt: string, chat: Partial<IChat> & Pick<IChat, "chatid">): Promise<IChat> => {
    const mutation = `
        mutation UpdateChat($chat: UpdateChatInput!) {
            updateChat(chat: $chat) {
                chatid
                email
                title
                favorite
                shareid
                created
                lastmessage
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                chat: {
                    chatid: chat.chatid,
                    title: chat.title,
                    favorite: chat.favorite,
                },
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const updated = result.data.updateChat;
    return {
        chatid: updated.chatid,
        user: updated.email,
        title: updated.title,
        favorite: updated.favorite,
        shareid: updated.shareid,
        messages: [],
        created: new Date(updated.created),
        lastmessage: new Date(updated.lastmessage),
    };
};

export const deleteChat = async (jwt: string, chatid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteChat($chatid: String!) {
            deleteChat(chatid: $chatid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                chatid,
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteChat;
};

export const getProjects = async (jwt: string): Promise<IProject[]> => {
    const query = `
        query GetProjects {
            projects {
                projectid
                email
                title
                favorite
                description
                instructions
                files {
                    uri
                }
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ query }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Map GraphQL response to IProject interface
    return result.data.projects.map(
        (project: {
            projectid: string;
            email: string;
            title: string;
            favorite: boolean;
            description?: string;
            instructions?: string;
            files: { uri: string }[];
            created: string;
        }): IProject => ({
            projectid: project.projectid,
            user: project.email,
            title: project.title,
            favorite: project.favorite,
            description: project.description,
            instructions: project.instructions,
            chats: [],
            created: new Date(project.created),
        })
    );
};

export const createProject = async (
    jwt: string,
    project: Pick<IProject, "title" | "description">
): Promise<IProject> => {
    const mutation = `
        mutation CreateProject($project: CreateProjectInput!) {
            createProject(project: $project) {
                projectid
                email
                title
                favorite
                description
                instructions
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                project: {
                    title: project.title,
                    description: project.description,
                },
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Map GraphQL response to IProject interface
    const created = result.data.createProject;
    return {
        projectid: created.projectid,
        user: created.email,
        title: created.title,
        favorite: created.favorite,
        description: created.description,
        instructions: created.instructions,
        chats: [],
        created: new Date(created.created),
    };
};

export const updateProject = async (
    jwt: string,
    project: Partial<IProject> & Pick<IProject, "projectid">
): Promise<IProject> => {
    const mutation = `
        mutation UpdateProject($project: UpdateProjectInput!) {
            updateProject(project: $project) {
                projectid
                email
                title
                favorite
                description
                instructions
                files {
                    uri
                }
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                project: {
                    projectid: project.projectid,
                    title: project.title,
                    favorite: project.favorite,
                    description: project.description,
                    instructions: project.instructions,
                },
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const updated = result.data.updateProject;
    return {
        projectid: updated.projectid,
        user: updated.email,
        title: updated.title,
        favorite: updated.favorite,
        description: updated.description,
        instructions: updated.instructions,
        chats: [],
        created: new Date(updated.created),
    };
};

export const deleteProject = async (jwt: string, projectid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteProject($projectid: String!) {
            deleteProject(projectid: $projectid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: {
                projectid,
            },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteProject;
};

export const createShare = async (jwt: string, chatid: string): Promise<string> => {
    const mutation = `
        mutation ShareChat($chatid: String!) {
            shareChat(chatid: $chatid) {
                shareid
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.shareChat.shareid;
};

export const deleteShare = async (jwt: string, chatid: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteShare($chatid: String!) {
            deleteShare(chatid: $chatid)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteShare;
};

export const getMessages = async (jwt: string, chatid: string): Promise<IMessage[]> => {
    const query = `
        query GetMessages($chatid: String!) {
            messages(chatid: $chatid) {
                messageid
                role
                content {
                    contentid
                    type
                    text
                    document {
                        name
                        format
                        source {
                            s3Location {
                                uri
                            }
                        }
                    }
                    image {
                        format
                        source {
                            s3Location {
                                uri
                            }
                        }
                    }
                    reasoningContent {
                        reasoningText {
                            text
                            signature
                        }
                    }
                    toolUse {
                        toolUseId
                        name
                        input
                    }
                    toolResult {
                        toolUseId
                        status
                        content {
                            text
                            json
                            document {
                                name
                                format
                                source {
                                    s3Location {
                                        uri
                                    }
                                }
                            }
                            image {
                                format
                                source {
                                    s3Location {
                                        uri
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { chatid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    // Map GraphQL response to IMessage[] interface
    return result.data.messages.map(
        (message: {
            messageid: string;
            role: "user" | "assistant";
            content: {
                contentid?: string;
                type?: string;
                text?: string;
                document?: {
                    name: string;
                    format: string;
                    source: { s3Location: { uri: string } };
                };
                image?: {
                    format: string;
                    source: { s3Location: { uri: string } };
                };
                reasoningContent?: {
                    reasoningText?: { text: string; signature?: string };
                };
                toolUse?: {
                    toolUseId: string;
                    name: string;
                    input: unknown;
                };
                toolResult?: {
                    toolUseId: string;
                    status?: string;
                    content: {
                        text?: string;
                        json?: unknown;
                        document?: {
                            name: string;
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                        image?: {
                            format: string;
                            source: { s3Location: { uri: string } };
                        };
                    }[];
                };
            }[];
        }): IMessage => ({
            messageid: message.messageid,
            role: message.role,
            content: message.content.map((block) => ({
                contentid: block.contentid ?? undefined,
                type: (block.type as IMessage["content"][0]["type"]) ?? undefined,
                text: block.text,
                document: block.document
                    ? {
                          name: block.document.name,
                          format: block.document.format as IMessage["content"][0]["document"] extends {
                              format: infer F;
                          }
                              ? F
                              : never,
                          source: { s3Location: block.document.source.s3Location },
                      }
                    : undefined,
                image: block.image
                    ? {
                          format: block.image.format as IMessage["content"][0]["image"] extends { format: infer F }
                              ? F
                              : never,
                          source: { s3Location: block.image.source.s3Location },
                      }
                    : undefined,
                reasoningContent: block.reasoningContent
                    ? {
                          reasoningText: block.reasoningContent.reasoningText
                              ? {
                                    text: block.reasoningContent.reasoningText.text,
                                    signature: block.reasoningContent.reasoningText.signature ?? undefined,
                                }
                              : undefined,
                      }
                    : undefined,
                toolUse: block.toolUse ?? undefined,
                toolResult: block.toolResult
                    ? {
                          toolUseId: block.toolResult.toolUseId,
                          status: block.toolResult.status as "success" | "error" | undefined,
                          content: block.toolResult.content.map((c) => ({
                              text: c.text,
                              json: c.json,
                              document: c.document
                                  ? {
                                        name: c.document.name,
                                        format: c.document.format as IMessage["content"][0]["document"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.document.source.s3Location },
                                    }
                                  : undefined,
                              image: c.image
                                  ? {
                                        format: c.image.format as IMessage["content"][0]["image"] extends {
                                            format: infer F;
                                        }
                                            ? F
                                            : never,
                                        source: { s3Location: c.image.source.s3Location },
                                    }
                                  : undefined,
                          })),
                      }
                    : undefined,
            })),
        })
    );
};

let cachedPrompts: IPrompt[] | null = null;

export const getPrompts = async (): Promise<IPrompt[]> => {
    if (cachedPrompts) {
        return cachedPrompts;
    }
    // TODO graphql query for this
    return new Promise((resolve) => {
        setTimeout(() => {
            const mockPrompts = generateMockPrompts();
            cachedPrompts = mockPrompts;
            resolve(mockPrompts);
        }, 1000);
    });
};

export const fetchStreamResponse = async (
    jwt: string,
    request: IChatRequest,
    abortSignal: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
    const response = await fetch(CHAT_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(request),
        signal: abortSignal,
    });

    if (!response.ok) {
        throw new Error(`Chat request failed: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
        throw new Error("No response body");
    }

    return response.body;
};

export const getSummary = async (jwt: string, text: string, length: number): Promise<ISummary> => {
    console.log(`${Date.now()} getSummary: text length=${text.length}, summary length=${length}`);

    const query = `
        query Summarize($text: String!, $length: Int!) {
            summarize(text: $text, length: $length)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { text, length },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return { summary: result.data.summarize || "" };
};

export const getShareById = async (jwt: string, shareid: string): Promise<IShare> => {
    const query = `
        query GetShareById($shareid: String!) {
            share(shareid: $shareid) {
                shareid
                chatid
                email
                messages {
                    messageid
                    role
                    content {
                        contentid
                        type
                        text
                        document {
                            name
                            format
                            source {
                                s3Location {
                                    uri
                                }
                            }
                        }
                        image {
                            format
                            source {
                                s3Location {
                                    uri
                                }
                            }
                        }
                        reasoningContent {
                            reasoningText {
                                text
                                signature
                            }
                        }
                        toolUse {
                            toolUseId
                            name
                            input
                        }
                        toolResult {
                            toolUseId
                            status
                            content {
                                text
                                json
                                document {
                                    name
                                    format
                                    source {
                                        s3Location {
                                            uri
                                        }
                                    }
                                }
                                image {
                                    format
                                    source {
                                        s3Location {
                                            uri
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                created
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { shareid },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    const share = result.data.share;

    if (!share) {
        throw new Error(`Share not found: ${shareid}`);
    }

    // Map GraphQL response to IShare interface
    return {
        shareid: share.shareid,
        chatid: share.chatid,
        email: share.email,
        messages: share.messages?.map(
            (message: {
                messageid: string;
                role: "user" | "assistant";
                content: {
                    contentid?: string;
                    type?: string;
                    text?: string;
                    document?: {
                        name: string;
                        format: string;
                        source: { s3Location: { uri: string } };
                    };
                    image?: {
                        format: string;
                        source: { s3Location: { uri: string } };
                    };
                    reasoningContent?: {
                        reasoningText?: { text: string; signature?: string };
                    };
                    toolUse?: {
                        toolUseId: string;
                        name: string;
                        input: unknown;
                    };
                    toolResult?: {
                        toolUseId: string;
                        status?: string;
                        content: {
                            text?: string;
                            json?: unknown;
                            document?: {
                                name: string;
                                format: string;
                                source: { s3Location: { uri: string } };
                            };
                            image?: {
                                format: string;
                                source: { s3Location: { uri: string } };
                            };
                        }[];
                    };
                }[];
            }): IMessage => ({
                messageid: message.messageid,
                role: message.role,
                content: message.content.map((block) => ({
                    contentid: block.contentid ?? undefined,
                    type: (block.type as IMessage["content"][0]["type"]) ?? undefined,
                    text: block.text,
                    document: block.document
                        ? {
                              name: block.document.name,
                              format: block.document.format as IMessage["content"][0]["document"] extends {
                                  format: infer F;
                              }
                                  ? F
                                  : never,
                              source: { s3Location: block.document.source.s3Location },
                          }
                        : undefined,
                    image: block.image
                        ? {
                              format: block.image.format as IMessage["content"][0]["image"] extends { format: infer F }
                                  ? F
                                  : never,
                              source: { s3Location: block.image.source.s3Location },
                          }
                        : undefined,
                    reasoningContent: block.reasoningContent
                        ? {
                              reasoningText: block.reasoningContent.reasoningText
                                  ? {
                                        text: block.reasoningContent.reasoningText.text,
                                        signature: block.reasoningContent.reasoningText.signature ?? undefined,
                                    }
                                  : undefined,
                          }
                        : undefined,
                    toolUse: block.toolUse ?? undefined,
                    toolResult: block.toolResult
                        ? {
                              toolUseId: block.toolResult.toolUseId,
                              status: block.toolResult.status as "success" | "error" | undefined,
                              content: block.toolResult.content.map((c) => ({
                                  text: c.text,
                                  json: c.json,
                                  document: c.document
                                      ? {
                                            name: c.document.name,
                                            format: c.document.format as IMessage["content"][0]["document"] extends {
                                                format: infer F;
                                            }
                                                ? F
                                                : never,
                                            source: { s3Location: c.document.source.s3Location },
                                        }
                                      : undefined,
                                  image: c.image
                                      ? {
                                            format: c.image.format as IMessage["content"][0]["image"] extends {
                                                format: infer F;
                                            }
                                                ? F
                                                : never,
                                            source: { s3Location: c.image.source.s3Location },
                                        }
                                      : undefined,
                              })),
                          }
                        : undefined,
                })),
            })
        ),
        created: new Date(share.created),
    };
};

export interface IPresignResult {
    presignedUrl: string;
    s3Uri: string;
}

/**
 * Get a presigned URL and S3 URI for uploading a file to S3
 */
export const getPresignedUrl = async (jwt: string, filename: string): Promise<IPresignResult> => {
    const query = `
        query Presign($filename: String!) {
            presign(filename: $filename) {
                presignedUrl
                s3Uri
            }
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query,
            variables: { filename },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.presign;
};

/**
 * Delete a file from S3
 */
export const deleteS3File = async (jwt: string, filename: string): Promise<boolean> => {
    const mutation = `
        mutation DeleteFile($filename: String!) {
            deleteFile(filename: $filename)
        }
    `;

    const response = await fetch(GRAPHQL_API_URL, {
        method: "POST",
        credentials: "omit",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
            query: mutation,
            variables: { filename },
        }),
    });

    if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
        throw new Error(`GraphQL error: ${result.errors[0]?.message || "Unknown error"}`);
    }

    return result.data.deleteFile;
};
